// src/pages/connection/Integracoes.tsx
import { useEffect, useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDatabase } from '@/icons';
import { useTheme } from '../../contexts/ThemeContext';
import { apiGet } from '../../services/api';
import { formatDateTime } from '../../utils/formatUtils';
import SidePanel from '../../components/common/SidePanel';
import TableIntegrationSidePanelContent from './TableIntegrationSidePanelContent';
import './Integracoes.css';
import { useAuth } from '../../contexts/AuthContext';
import { showAlert } from '../../contexts/DialogContext';
import { logger } from '@/utils/logger';

interface TableData {
  nomeTabela: string;
  quantidadeLinhas: number;
  dataIntegracao: string | null;
}

const sortAlphabetically = (items: TableData[]) =>
  [...items].sort((a, b) => a.nomeTabela.localeCompare(b.nomeTabela, 'pt-BR'));

const Integracoes = () => {
  const { theme } = useTheme();
  const { hasPermission } = useAuth();
  const [tables, setTables] = useState<TableData[]>([]);
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
  const [selectedTableName, setSelectedTableName] = useState<string | null>(null);
  const [panelKey, setPanelKey] = useState(0); // Chave para forçar reinício
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.title = 'Bluelt | Integrações';

    const fetchTables = async () => {
      try {
        const response = await apiGet('/v1/integracoes/tabelas?page=0&size=10');
        setTables(response.data.content);
        setLoading(false);
      } catch (err) {
        logger.error('Erro ao buscar tabelas:', err);
        setError('Falha ao carregar integrações. Tente novamente.');
        setLoading(false);
      }
    };

    fetchTables();
  }, []);

  const toggleSidePanel = useCallback(() => {
    setIsSidePanelOpen(prev => {
      if (!prev) {
        setPanelKey(prevKey => prevKey + 1); // Incrementa ao abrir
        setSelectedTableName(null); // Limpa ao abrir
      } else {
        setSelectedTableName(null); // Limpa ao fechar
      }
      return !prev;
    });
  }, []);

  const requiredPermission = 'modulo.integracoes';
  if (!hasPermission(requiredPermission)) {
    return <div>Você não tem permissão para acessar esta página.</div>;
  }

  return (
    <div className={`integracoes-page ${theme}`}>
      <main className="integracoes-main">
        <h1>
        <FontAwesomeIcon icon={faDatabase} /> Integrações
        </h1>
      </main>
      {loading && <p>Carregando...</p>}
      {error && <p className="error-message">{error}</p>}
      {!loading && !error && tables.length === 0 && <p>Nenhuma integração encontrada.</p>}
      {!loading && !error && tables.length > 0 && (
        <div className="cards-wrapper-integration">
          <div className="cards-container-integration">
            {sortAlphabetically(tables).map((table, index) => (
              <div
                key={index}
                className={`integration-card ${table.quantidadeLinhas === 0 ? 'invalid-card' : ''}`}
                onClick={async () => {
                  if (table.quantidadeLinhas === 0) {
                    await showAlert('Tabela vazia ou com erro!', { variant: 'warning' });
                    return;
                  }
                  setSelectedTableName(table.nomeTabela);
                  setIsSidePanelOpen(true);
                }}
              >
                <span className="card-label">{table.nomeTabela}</span>
                <p>Total de linhas: {table.quantidadeLinhas}</p>
                <p>
                  Data da Integração:{' '}
                  {table.dataIntegracao ? formatDateTime(table.dataIntegracao) : ''}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
      <SidePanel
        key={panelKey} // Força remount
        isOpen={isSidePanelOpen}
        onClose={toggleSidePanel}
        theme={theme}
      >
        <TableIntegrationSidePanelContent
          tableName={selectedTableName}
          onClose={toggleSidePanel}
          fetchInitialData={true}
        />
      </SidePanel>
    </div>
  );
};

export default Integracoes;